import React from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import { IMG, SpeakCta, FeatureList, ASSESSMENT_URL, DEMO_URL } from './shared';

/** Product card shell shared by every product block on the page. Passing `bg`
 *  switches the card to the dark art-backed treatment (CSS vars flip via the
 *  mpa-card--dark scope). */
export const ProductShell = ({
  name,
  logo,
  bg,
  bgSubtle,
  tagline,
  description,
  descriptionStyle,
  subhead,
  features,
  extra,
  apiLink,
  footnote,
  closingLine,
  headerRight,
  aside,
  asideWide,
  tinted,
}) => {
  const cardClass = `mpa-card${bg ? ' mpa-card--dark' : ''}${!bg && tinted ? ' mpa-card--tinted' : ''}`;
  const cardStyle = bg
    ? {
        backgroundColor: '#050505',
        backgroundImage: bgSubtle
          ? `linear-gradient(to bottom, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.7) 55%, rgba(5,5,5,0.4) 85%, rgba(5,5,5,0.2) 100%), url(${bg})`
          : `linear-gradient(to bottom, rgba(5,5,5,0.82) 0%, rgba(5,5,5,0.45) 42%, rgba(5,5,5,0.12) 80%, rgba(5,5,5,0) 100%), url(${bg})`,
        backgroundSize: bgSubtle ? '100% 100%, 100% 130%' : '100% 100%, 100% 150%',
        backgroundPosition: 'center, center bottom',
        backgroundRepeat: 'no-repeat, no-repeat',
      }
    : undefined;

  const apiHref = apiLink?.href || ASSESSMENT_URL;
  const apiExternal = /^https?:\/\//.test(apiHref);

  return (
    <div className="mpa-container mpa-block">
      <div className={cardClass} style={cardStyle}>
        {/* Identity — logo on the left, optional visual group on the right */}
        <div className="mpa-card-head">
          <img src={logo} alt={`${name.replace(/™/g, '')} logo`} className="mpa-card-logo" />
          {headerRight}
        </div>

        <div className={aside ? 'mpa-card-body' : undefined}>
          {/* Left column — all textual content */}
          <div className={aside ? 'mpa-card-main' : undefined}>
            <h3 className="mpa-card-tagline">{tagline}</h3>
            <p className="mpa-card-desc" style={descriptionStyle}>
              {description}
            </p>

            {extra && <div className="mpa-card-extra">{extra}</div>}

            {features && (
              <div className="mpa-card-features">
                {subhead && <p className="mpa-card-subhead">{subhead}</p>}
                <FeatureList items={features} />
              </div>
            )}

            {closingLine && <p className="mpa-card-closing">{closingLine}</p>}

            <div className="mpa-card-ctas">
              <SpeakCta href={DEMO_URL} label="Book Demo" />
              {apiLink &&
                (apiExternal ? (
                  <a href={apiHref} target="_blank" rel="noopener noreferrer" className="mpa-api-link">
                    {apiLink.label} <span aria-hidden="true">&rarr;</span>
                  </a>
                ) : (
                  <Link to={apiHref} className="mpa-api-link">
                    {apiLink.label} <span aria-hidden="true">&rarr;</span>
                  </Link>
                ))}
            </div>

            {footnote && <p className="mpa-footnote">{footnote}</p>}
          </div>

          {/* Right column — optional visual aside (e.g. mobile video) */}
          {aside && (
            <div className={`mpa-card-aside${asideWide ? ' mpa-card-aside--wide' : ''}`}>{aside}</div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Phone-shaped frame for the product walkthrough videos. Pass `videoSrc`
 *  (portrait ~9:19 MP4) to play it in the frame — muted/looped/inline so
 *  browsers allow autoplay; falls back to the "coming soon" placeholder.
 *  fit="contain" letterboxes on black instead of cropping — use it for clips
 *  whose aspect ratio differs from the 9:19 frame and has edge content. */
export const MobileVideoMock = ({ videoSrc, poster, fit = 'cover' }) => (
  <div className="mpa-phone">
    <div className="mpa-phone-notch" />
    {videoSrc ? (
      <video
        className="mpa-phone-video"
        src={videoSrc}
        poster={poster}
        style={{ objectFit: fit }}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    ) : (
      <div className="mpa-phone-screen">
        <span className="mpa-phone-play">
          <Play size={28} />
        </span>
        <span className="mpa-phone-caption">Video coming soon</span>
      </div>
    )}
  </div>
);

const GUESTS = [
  { src: `${IMG}/guests/leisure.webp`, label: 'Leisure', alt: 'Leisure rental customer' },
  { src: `${IMG}/guests/rideshare.webp`, label: 'Rideshare', alt: 'Rideshare driver' },
  { src: `${IMG}/guests/corporate.webp`, label: 'Corporate', alt: 'Corporate rental customer' },
  { src: `${IMG}/guests/delivery.webp`, label: 'Delivery', alt: 'Food delivery courier' },
  { src: `${IMG}/guests/packages.webp`, label: 'Packages', alt: 'Package delivery driver' },
];

const DEVICES = [
  { src: `${IMG}/devices/desktop.webp`, label: 'Desktop', size: 28 },
  { src: `${IMG}/devices/tablet.webp`, label: 'Tablet', size: 24 },
  { src: `${IMG}/devices/mobile.webp`, label: 'Mobile', size: 20 },
];

/** Guest avatars → arrow → device mockups, with hover labels. */
export const GuestExperienceRow = () => (
  <div className="mpa-guestrow">
    <div className="mpa-guestrow-group">
      {GUESTS.map((guest, i) => (
        <div
          key={guest.label}
          className="mpa-avatar"
          style={{ marginLeft: i === 0 ? 0 : '-14px', zIndex: GUESTS.length - i }}
        >
          <div className="mpa-avatar-ring">
            <img src={guest.src} alt={guest.alt} width={48} height={48} />
          </div>
          <span className="mpa-avatar-label">{guest.label}</span>
        </div>
      ))}
    </div>

    <ArrowRight className="mpa-guest-arrow" size={24} strokeWidth={2.5} aria-hidden="true" />

    <div className="mpa-guestrow-group">
      {DEVICES.map((device, i) => (
        <div
          key={device.label}
          className="mpa-avatar"
          style={{ marginLeft: i === 0 ? 0 : '-14px', zIndex: DEVICES.length - i }}
        >
          <div className="mpa-avatar-ring mpa-avatar-ring--device">
            <img
              src={device.src}
              alt={`${device.label} device`}
              style={{ width: device.size, height: device.size, objectFit: 'contain' }}
            />
          </div>
          <span className="mpa-avatar-label">{device.label}</span>
        </div>
      ))}
    </div>
  </div>
);
